import { type ReactNode } from "react";

import { useAgentThreadSearch } from "@/shared/domain/agentThread/hooks";
import { Card } from "@/shared/foundation/primitives";
import type { SearchQueryBody } from "@/types";

import { RecentThreadsTable } from "./RecentThreadsTable";
import styles from "./sections.module.css";

const RECENT_THREAD_COUNT = 5;

export interface RecentThreadsSectionProps {
  agentId: string;
  /** The org the agent, and so its threads, belong to. */
  orgId: string;
}

/**
 * The last few threads this agent started, newest first, with the way to
 * every one of them under the list.
 */
export function RecentThreadsSection({
  agentId,
  orgId,
}: RecentThreadsSectionProps) {
  const query: SearchQueryBody = {
    condition: {
      field: "created_from_agent_id",
      comparator: "EQUALS",
      value: agentId,
    },
    sort_by: "created",
    sort_direction: "DESC",
    limit: RECENT_THREAD_COUNT,
  };
  const search = useAgentThreadSearch({ orgId, query });
  const threads = search.data?.items;

  let body: ReactNode;
  if (search.isError) {
    body = (
      <p className={styles.error} data-testid="agent-detail-threads-error">
        Failed to load threads. {search.error.message}
      </p>
    );
  } else if (threads?.length === 0) {
    body = (
      <div
        className={styles.emptyState}
        data-testid="agent-detail-threads-empty"
      >
        <div className={styles.emptyText}>
          <p className={styles.emptyTitle}>No threads yet</p>
          <p className={styles.muted}>
            Each run of this agent starts a thread.
          </p>
        </div>
      </div>
    );
  } else {
    body = <RecentThreadsTable agentId={agentId} threads={threads} />;
  }

  return (
    <Card.Root
      size="section"
      data-testid="agent-detail-threads"
      data-count={threads?.length}
    >
      <Card.Header>
        <Card.Title>Recent threads</Card.Title>
      </Card.Header>
      <Card.Body>{body}</Card.Body>
    </Card.Root>
  );
}
